import { useEffect, useState, useCallback } from "react";
import { Loader2, SlidersHorizontal, Sparkles } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import DealCard from "@/components/DealCard";
import DealDetailSheet from "@/components/DealDetailSheet";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { fetchDeals, fetchCategories, scoreAll } from "@/lib/api";

const SORderS = [
  { value: "score", label: "KI-Score" },
  { value: "discount", label: "Rabatt" },
  { value: "newest", label: "Neueste" },
];
const STATUSES = [
  { value: "Alle", label: "Alle Status" },
  { value: "new", label: "Neu" },
  { value: "saved", label: "Gespeichert" },
  { value: "posted", label: "Gepostet" },
];

export default function Deals() {
  const [deals, setDeals] = useState([]);
  const [categories, setCategories] = useState(["Alle"]);
  const [loading, setLoading] = useState(true);
  const [scoring, setScoring] = useState(false);

  const [minDiscount, setMinDiscount] = useState(10);
  const [category, setCategory] = useState("Alle");
  const [status, setStatus] = useState("Alle");
  const [sort, setSort] = useState("score");

  const [selected, setSelected] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchDeals({ min_discount: minDiscount, category, status, sort });
      setDeals(data);
    } finally {
      setLoading(false);
    }
  }, [minDiscount, category, status, sort]);

  useEffect(() => { fetchCategories().then((d) => setCategories(d.categories)); }, []);
  useEffect(() => { load(); }, [load]);

  const handleScoreAll = async () => {
    setScoring(true);
    try {
      const res = await scoreAll();
      toast.success(`${res.scored} Deals bewertet`);
      await load();
    } catch (e) {
      toast.error("KI-Bewertung fehlgeschlagen");
    } finally {
      setScoring(false);
    }
  };

  const openDeal = (deal) => { setSelected(deal); setSheetOpen(true); };

  const onChanged = (updated) => {
    if (!updated) { load(); return; }
    setDeals((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));
    setSelected(updated);
  };

  return (
    <div data-testid="deals-page">
      <PageHeader title="Deals" subtitle="KI-gerankte Schnäppchen aus deinen Quellen">
        <Button
          onClick={handleScoreAll}
          disabled={scoring}
          className="rounded-full ai-gradient-bg text-white hover:opacity-90 active:scale-95 transition-all"
          data-testid="deals-score-all"
        >
          {scoring ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          Alle bewerten
        </Button>
      </PageHeader>

      <div className="px-6 md:px-10 py-8">
        {/* Filters */}
        <div className="bg-white rounded-2xl border border-black/5 shadow-[0_2px_10px_rgba(0,0,0,0.02)] p-5 mb-6">
          <div className="flex items-center gap-2 mb-4 text-[#86868B]">
            <SlidersHorizontal className="h-4 w-4" strokeWidth={2} />
            <span className="text-xs font-semibold uppercase tracking-wide">Filter</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-5 items-end">
            <div className="md:col-span-1">
              <label className="text-sm font-medium text-[#1D1D1F] flex justify-between mb-3">
                Mind. Rabatt <span className="text-[#0071E3] font-semibold">{minDiscount}%</span>
              </label>
              <Slider
                value={[minDiscount]}
                min={10}
                max={70}
                step={5}
                onValueChange={(v) => setMinDiscount(v[0])}
                data-testid="filter-discount-slider"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#1D1D1F] block mb-2">Kategorie</label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="rounded-xl bg-[#F5F5F7] border-0" data-testid="filter-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-[#1D1D1F] block mb-2">Status</label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="rounded-xl bg-[#F5F5F7] border-0" data-testid="filter-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-[#1D1D1F] block mb-2">Sortieren</label>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger className="rounded-xl bg-[#F5F5F7] border-0" data-testid="filter-sort">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SORderS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-32 text-[#86868B]">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : deals.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-black/5">
            <img src="https://static.prod-images.emergentagent.com/jobs/4ca59440-3756-4a33-9153-174842ff835f/images/f3df7140de241ba362f5b7d787522201e3bdc4c710a971bcf75cccc27d55c611.png" alt="Keine Deals" className="max-h-44 mx-auto mb-4" />
            <p className="font-heading font-semibold text-[#1D1D1F]">Keine Deals gefunden</p>
            <p className="text-sm text-[#86868B] mt-1">Passe die Filter an, um mehr Deals zu sehen.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="deals-grid">
            {deals.map((deal, i) => (
              <DealCard key={deal.id} deal={deal} index={i} onClick={openDeal} />
            ))}
          </div>
        )}
      </div>

      <DealDetailSheet deal={selected} open={sheetOpen} onOpenChange={setSheetOpen} onChanged={onChanged} />
    </div>
  );
}
